import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  CheckSquare,
  ListTodo,
  Users,
  Contact,
  Megaphone,
  Settings,
  LogOut,
  ExternalLink,
  UserCog,
  ClipboardList,
  X,
  Wallet,
  TrendingUp,
  FileInput,
  FileOutput,
  Briefcase,
  Scale,
  Package,
  CalendarClock
} from 'lucide-react';
import { Button } from '../ui/button';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, isManager, hasOpenClawAccess } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('dashboard') },
    { to: '/tasks', icon: CheckSquare, label: t('myTasks') },
    { to: '/assigned', icon: ListTodo, label: t('assignedTasks') },
    { to: '/checklists', icon: ClipboardList, label: t('checklists') },
    { to: '/contacts', icon: Contact, label: t('contacts') },
    { to: '/updates', icon: Megaphone, label: t('updates') },
  ];

  if (isAdmin) {
    navItems.push({ to: '/users', icon: UserCog, label: t('users') });
  }

  navItems.push({ to: '/settings', icon: Settings, label: t('settings') });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] text-white flex flex-col border-r border-slate-800 transition-transform duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_ee8c2b19-0ad5-4e7a-85a8-9be181e48662/artifacts/l494p58g_rt.png" 
              alt="Logo"
              className="h-8 object-contain"
            />
            <span className="font-bold text-lg font-[Manrope]">Hub</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden text-slate-400 hover:text-white hover:bg-white/10"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D92632] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{t(user?.role)}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive 
                  ? "bg-white/10 text-white" 
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              )}
              data-testid={`nav-${item.to.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="h-5 w-5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
          
          {/* External Links Section */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <p className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {t('externalSystems')}
            </p>
            
            <a
              href="https://salary.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-payroll"
            >
              <Wallet className="h-5 w-5" strokeWidth={1.5} />
              {t('payroll')}
            </a>
            
            <a
              href="https://sales.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-sales-crm"
            >
              <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
              {t('salesCRM')}
            </a>
            
            <a
              href="https://accounting.kaufmanns-consulting.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-accounting-in"
            >
              <FileInput className="h-5 w-5" strokeWidth={1.5} />
              {t('invoicesIncoming')}
            </a>
            
            <a
              href="https://accounting1.kaufmanns-consulting.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-accounting-out"
            >
              <FileOutput className="h-5 w-5" strokeWidth={1.5} />
              {t('invoicesOutgoing')}
            </a>
            
            <a
              href="https://jobs-kaufmanns-consulting.com/admin/users/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-job-portal"
            >
              <Briefcase className="h-5 w-5" strokeWidth={1.5} />
              {t('jobPortal')}
            </a>
            
            <a
              href="https://inventar.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="nav-inventory"
            >
              <Package className="h-5 w-5" strokeWidth={1.5} />
              {t('inventory')}
            </a>
            
            {hasOpenClawAccess && (
              <a
                href="https://openclaw.kaufmanns-consulting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
                data-testid="nav-openclaw"
              >
                <Scale className="h-5 w-5" strokeWidth={1.5} />
                {t('openClaw')}
              </a>
            )}
            
            {isAdmin && (
              <a
                href="https://docs.google.com/spreadsheets/d/1l4CTXVm3KDZ1SSeTDO9oEUArmUplUq7CC92SeI6bEng/edit?gid=397451541#gid=397451541"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
                data-testid="nav-project-mc"
              >
                <CalendarClock className="h-5 w-5" strokeWidth={1.5} />
                {t('projectMCSchedule')}
              </a>
            )}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.5} />
            {t('logout')}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
